"use client";

import { NumberField } from "@base-ui/react/number-field";
import { MinusIcon, PlusIcon } from "lucide-react";

import { cx } from "../../cx";
import styles from "./number-stepper.module.css";

export interface NumberStepperProps {
  value: number;
  /** Fires the new absolute value (already clamped to min/max). */
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: "default" | "sm";
  /** `joined`: field and buttons as one bordered box. `round`: two round
   *  buttons either side of a borderless count. */
  shape?: "joined" | "round";
  disabled?: boolean;
  /** Names the field. */
  "aria-label"?: string;
  /** The minus and plus buttons' names, already translated. */
  decrementLabel: string;
  incrementLabel: string;
  /** Refuses every increase (button, arrow key, typed value) while "+"
   *  looks and announces as disabled. The field is the one tab stop (Base UI
   *  sets `tabIndex: -1` on both buttons), and it carries
   *  `incrementDescription`, so a keyboard user still hears why. */
  incrementDisabled?: boolean;
  /** Id of the element that explains `incrementDisabled`; it describes the
   *  "+" button and the field. */
  incrementDescription?: string;
  /** A refused increase, so the caller can re-announce its reason. */
  onIncrementRefused?: () => void;
  className?: string;
}

/** A small bounded integer: a typeable field joined to a minus and a plus. */
export function NumberStepper({
  value,
  onValueChange,
  min = 0,
  max,
  step = 1,
  size = "default",
  shape = "joined",
  disabled,
  "aria-label": ariaLabel,
  decrementLabel,
  incrementLabel,
  incrementDisabled = false,
  incrementDescription,
  onIncrementRefused,
  className,
}: NumberStepperProps) {
  return (
    <NumberField.Root
      value={value}
      onValueChange={(next, details) => {
        if (incrementDisabled && next !== null && next > value) {
          details.cancel();
          onIncrementRefused?.();
          return;
        }
        onValueChange(next ?? min);
      }}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      data-tui=""
      data-slot="number-stepper"
      data-size={size}
      data-shape={shape}
      className={cx(styles.root, className)}
    >
      <StepperGroup
        fieldLabel={ariaLabel}
        decrementLabel={decrementLabel}
        incrementLabel={incrementLabel}
        refusal={
          incrementDisabled
            ? { "aria-describedby": incrementDescription }
            : undefined
        }
      />
    </NumberField.Root>
  );
}

/** `refusal` is spread only when set: an `undefined` `aria-disabled` would
 *  override the one Base UI sets at `max`. */
function StepperGroup({
  fieldLabel,
  decrementLabel,
  incrementLabel,
  refusal,
}: {
  fieldLabel: string | undefined;
  decrementLabel: string;
  incrementLabel: string;
  refusal: { "aria-describedby": string | undefined } | undefined;
}) {
  return (
    <NumberField.Group className={styles.group}>
      <NumberField.Decrement
        aria-label={decrementLabel}
        className={styles.button}
      >
        <MinusIcon strokeWidth={2} />
      </NumberField.Decrement>
      <NumberField.Input
        aria-label={fieldLabel}
        {...refusal}
        className={styles.input}
      />
      <NumberField.Increment
        aria-label={incrementLabel}
        {...(refusal ? { ...refusal, "aria-disabled": true } : {})}
        className={styles.button}
      >
        <PlusIcon strokeWidth={2} />
      </NumberField.Increment>
    </NumberField.Group>
  );
}
